import React, { useState } from 'react';
import type { FieldValidator } from 'final-form';
import { Field, FieldRenderProps, FieldProps } from 'react-final-form';
import type { DropdownV3Props } from '../DropdownV3';
import DropdownV3 from '../DropdownV3';
import transformValidateProp from '../../util/transformValidateProp';

export type DropdownV3AdapterProps = FieldRenderProps<string, HTMLElement> & DropdownV3Props;

/**
 * ```import FinalFormDropdown from '@zola/zola-ui/src/components/Form/dropdownV3/FinalFormDropdown';
 *
 * Use with `react-final-form`. Wraps <Field /> and <DropdownV3 />
 */
const DropdownV3Adapter = ({
  input,
  meta,
  options,
  onSelect,
  ...rest
}: DropdownV3AdapterProps): JSX.Element => {
  const [value, setValue] = useState<string | number>('');
  const errorMessage = meta && meta.touched && meta.error ? meta.error : '';

  if (input && input.value !== value) {
    setValue(input.value);
  }

  return (
    <DropdownV3
      options={options}
      errorMessage={errorMessage}
      onSelect={(value) => {
        if (onSelect) {
          onSelect(value);
        }
        setValue(value || '');
        input.onChange(value);
      }}
      {...input}
      {...rest}
      value={value}
    />
  );
};

export interface FinalFormDropdownProps<
  RP extends FieldRenderProps<string, T>,
  T extends HTMLElement = HTMLElement
> extends Omit<FieldProps<string, RP, T>, 'validate' | 'type'> {
  name: string;
  validate?: FieldValidator<string>[] | FieldValidator<string> | null;
}
const FinalFormDropdown = <
  RP extends FieldRenderProps<string, T>,
  T extends HTMLElement = HTMLElement
>({
  validate,
  ...rest
}: FinalFormDropdownProps<RP, T>): React.ReactElement => {
  const composedValidators = transformValidateProp(validate);

  return <Field component={DropdownV3Adapter} validate={composedValidators} {...rest} />;
};

export default FinalFormDropdown;
